<script lang="ts" module>
  // Live-cue banner — the design-system live-cue primitive
  // (wiki/feature-design-system.md §Component primitives + §Typography, AC4).
  //
  // HIERARCHY (load-bearing, wiki §Hierarchy principle): the read-aloud script
  // is primary; the live cue is GLANCEABLE SECONDARY. The cue signals "live now"
  // through the accent pip + label + colour — NOT through size. The headline is
  // the `cue` type token (clamp(24,2.8vw,30) — deliberately smaller than the
  // display title) so it never out-shouts the script.
  //
  // Composes: a breathing accent pip + "cue live now" kicker, an optional
  // count, an optional shift/key line, the Patrick Hand headline, the coaching
  // imperative, and an optional energy-envelope sweep + "what to do now"
  // bullets. The envelope + bullets are OPTIONAL typed data, never slots.

  /** A single "what to do now" bullet (label + optional emphasis suffix). */
  export interface CueBullet {
    /** The bolded action word (accent-tinted), e.g. "volume". */
    label: string;
    /** Optional suffix shown after the label, e.g. "↑↑". */
    detail?: string;
  }

  /** Optional energy-envelope descriptor for the sweep bar. */
  export interface CueEnvelope {
    /** Left caption, e.g. "energy envelope" / "loudness target". */
    caption: string;
    /** Right accent value, e.g. "rising → peak 9" / "now → +6 dB". */
    value: string;
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  interface Props {
    /** The "live now" kicker text; omit for the localized "cue live now". */
    liveLabel?: string | null;
    /** Optional count caption, e.g. "04 / 09". */
    count?: string | null;
    /**
     * Optional shift/key line above the headline. Pass as a snippet so callers
     * can render the `Friend → Motivator` arrow markup or `vocal key · Volume`.
     */
    shift?: Snippet;
    /** The cue headline (Patrick Hand, `cue` size — glanceable secondary). */
    headline: string;
    /** The coaching imperative under the headline. */
    coaching?: string | null;
    /** Optional energy-envelope sweep. */
    envelope?: CueEnvelope | null;
    /** Optional "what to do now" bullets. */
    bullets?: CueBullet[];
    /** Reduced-motion — drops the breathing/sweep loops, keeps the state. */
    reducedMotion?: boolean;
    /** Frozen (paused take) — also drops the loops. */
    paused?: boolean;
    class?: string;
  }

  let {
    liveLabel = null,
    count = null,
    shift,
    headline,
    coaching = null,
    envelope = null,
    bullets = [],
    reducedMotion = false,
    paused = false,
    class: klass = '',
  }: Props = $props();

  const still = $derived(reducedMotion || paused);
</script>

<section class="vd-cue {klass}" class:vd-cue--still={still} aria-live="polite">
  <div class="vd-cue__meta">
    <span class="vd-cue__live">
      <span class="vd-cue__pip" aria-hidden="true"></span>
      {liveLabel ?? t(UiKeys.PracticeView.CueLiveNow)}
    </span>
    {#if count}<span class="vd-cue__count">{count}</span>{/if}
  </div>

  {#if shift}
    <div class="vd-cue__shift">{@render shift()}</div>
  {/if}

  <h2 class="vd-cue__headline">{headline}</h2>

  {#if coaching}
    <p class="vd-cue__imp">{coaching}</p>
  {/if}

  {#if envelope}
    <div class="vd-cue__env">
      <div class="vd-cue__env-top">
        <span>{envelope.caption}</span>
        <b>{envelope.value}</b>
      </div>
      <div class="vd-cue__env-track">
        <span class="vd-cue__env-fill"></span>
        <span class="vd-cue__env-head"></span>
      </div>
    </div>
  {/if}

  {#if bullets.length > 0}
    <div class="vd-cue__bullets">
      {#each bullets as bullet (bullet.label)}
        <span><b>{bullet.label}</b>{#if bullet.detail}&nbsp;{bullet.detail}{/if}</span>
      {/each}
    </div>
  {/if}
</section>

<style lang="scss">
  .vd-cue {
    padding: 24px 0;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
    position: relative;
  }

  .vd-cue__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .vd-cue__live {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent-text);
  }

  .vd-cue__pip {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--accent);
    position: relative;

    // The breathing ring — signals "live now" (wiki §Motion).
    &::after {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      border: 1px solid var(--accent);
      animation: vd-breathe 2.4s ease-out infinite;
    }
  }

  @keyframes vd-breathe {
    0% {
      transform: scale(0.8);
      opacity: 0.7;
    }
    100% {
      transform: scale(1.8);
      opacity: 0;
    }
  }

  .vd-cue__count {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--ink-mute);
    font-variant-numeric: tabular-nums;
  }

  .vd-cue__shift {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-mute);
    margin-bottom: 8px;

    :global(b) {
      color: var(--ink);
    }
    :global(.arr) {
      color: var(--accent-text);
    }
  }

  // Glanceable SECONDARY — the cue type token, deliberately smaller than the
  // display title so it never out-shouts the read-aloud script.
  .vd-cue__headline {
    font-family: var(--font-hand);
    font-size: var(--text-cue);
    line-height: var(--text-cue--line-height);
  }

  .vd-cue__imp {
    margin-top: 10px;
    font-family: var(--font-ui);
    font-size: var(--text-body);
    font-weight: 400;
    color: var(--ink-soft);
    line-height: var(--text-body--line-height);
    max-width: 50ch;
  }

  .vd-cue__env {
    margin-top: 20px;
  }

  .vd-cue__env-top {
    display: flex;
    justify-content: space-between;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-mute);
    margin-bottom: 8px;

    b {
      color: var(--accent-text);
    }
  }

  .vd-cue__env-track {
    height: 2px;
    background: var(--line);
    position: relative;
  }

  .vd-cue__env-fill {
    position: absolute;
    inset: 0 auto 0 0;
    height: 2px;
    background: var(--accent);
    width: 30%;
    animation: vd-swell 4.5s ease-in-out infinite;
  }

  .vd-cue__env-head {
    position: absolute;
    top: -3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent);
    left: 30%;
    animation: vd-swellhead 4.5s ease-in-out infinite;
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 18%, transparent);
  }

  @keyframes vd-swell {
    0% {
      width: 28%;
    }
    45% {
      width: 86%;
    }
    70% {
      width: 62%;
    }
    100% {
      width: 28%;
    }
  }
  @keyframes vd-swellhead {
    0% {
      left: 28%;
    }
    45% {
      left: 86%;
    }
    70% {
      left: 62%;
    }
    100% {
      left: 28%;
    }
  }

  .vd-cue__bullets {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 16px;
    font-family: var(--font-ui);
    font-size: var(--text-meta);
    font-weight: 700;
    color: var(--ink-soft);

    b {
      color: var(--accent-text);
      font-weight: 700;
    }
  }

  // Reduced motion / paused — drop the loops, keep the state (wiki §Motion).
  .vd-cue--still {
    .vd-cue__pip::after,
    .vd-cue__env-fill,
    .vd-cue__env-head {
      animation: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .vd-cue__pip::after,
    .vd-cue__env-fill,
    .vd-cue__env-head {
      animation: none;
    }
  }
</style>
